'use client';

import { ScrollSmoother } from '@lib/tools/gsap-init';
import cn from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import styles from './Calculator.module.scss';
import { ComparingModal } from './comparing-modal/ComparingModal';
import { Filters } from './filters-new-calculator/Filters';
import { useFinchData } from './finch-program/useFinchData';
import { FormMortgageModal } from './form-mortgage-modal/FormMortgageModal';
import { ProgramsList } from './programs-list/ProgramsList';
const initialFilters = {
  realtyCost: 10,
  period: 20,
  creditAmount: 0,
  initialPayment: 0,
  age: 25,
  personalInsurance: 1
};
const LEGACY_MORTGAGE_GROUP_OPTIONS = [{
  label: 'Семейная',
  value: 'isFamily'
}];
const createProgramTypes = mortgageGroupOptions => [{
  label: 'Все программы',
  value: null,
  active: true
}, ...mortgageGroupOptions.map(option => ({
  ...option,
  active: false
}))];
const NewCalculator = ({
  flat = false,
  mortgageGroupOptions = LEGACY_MORTGAGE_GROUP_OPTIONS
}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [comparings, setComparings] = useState([]);
  const [programTypes, setProgramTypes] = useState(() => createProgramTypes(mortgageGroupOptions));
  const [sortingProgram, setSortingProgram] = useState([{
    label: 'Ставка по возрастанию',
    value: 'rate_asc',
    active: true
  }, {
    label: 'Ставка по убыванию',
    value: 'rate_desc',
    active: false
  }, {
    label: 'Платеж по возрастанию',
    value: 'payment_asc',
    active: false
  }, {
    label: 'Платеж по убыванию',
    value: 'payment_desc',
    active: false
  }]);
  const [isShowCompareModal, setIsShowCompareModal] = useState({
    mounted: false,
    visible: false
  });
  const [isShowFormModal, setIsShowFormModal] = useState({
    mounted: false,
    visible: false
  });
  const [modalFormData, setModalFormDataData] = useState({});
  const {
    programs,
    ranges,
    isLoading
  } = useFinchData(filters, programTypes);
  const modalFormName = flat ? 'mortgageFinchFlatForm' : 'mortgageFinchForm';
  useEffect(() => {
    if (!isShowCompareModal.visible) {
      document.body.style.overflow = 'auto';
      return;
    }
    if (isShowCompareModal.visible) {
      document.body.style.overflow = 'hidden';
      const activeSort = sortingProgram.find(p => p.active)?.value;
      setComparings(list => sortComparingsHelper(list, activeSort));
    }
  }, [isShowCompareModal.visible, sortingProgram]);
  useEffect(() => {
    if (!isShowFormModal.visible) {
      document.body.style.overflow = 'auto';
      setModalFormDataData({});
      return;
    }
    if (isShowFormModal.visible) {
      document.body.style.overflow = 'hidden';
    }
  }, [isShowFormModal.visible]);
  useEffect(() => {
    if (isLoading) {
      // при загрузке данных очищаем сравнения, т.к. они будут неактуальны
      setComparings([]);
    }
  }, [isLoading]);
  useEffect(() => {
    // дает возможность закрепить comparingsBtns через sticky
    const smoother = ScrollSmoother.get();
    if (!smoother) {
      return undefined;
    }
    smoother.kill();
    smoother.wrapper().style.overflow = 'visible';
    smoother.content().style.overflow = 'visible';
    return () => {
      smoother.refresh();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };
  }, []);
  const sortComparingsHelper = (list_0, sortValue) => {
    const SORTERS = {
      rate_asc: (a, b) => a.aggregateValues.interestRateActual - b.aggregateValues.interestRateActual,
      rate_desc: (a_0, b_0) => b_0.aggregateValues.interestRateActual - a_0.aggregateValues.interestRateActual,
      payment_asc: (a_1, b_1) => a_1.aggregateValues.monthPayment - b_1.aggregateValues.monthPayment,
      payment_desc: (a_2, b_2) => b_2.aggregateValues.monthPayment - a_2.aggregateValues.monthPayment
    };
    return SORTERS[sortValue] ? [...list_0].sort(SORTERS[sortValue]) : list_0;
  };
  const onSelectProgram = useCallback((_, programItem) => {
    setProgramTypes(prev => prev.map(p_0 => p_0.value === programItem.value ? {
      ...p_0,
      active: true
    } : {
      ...p_0,
      active: false
    }));
  }, []);
  const onSelectSortComparings = useCallback((__0, sortItem) => {
    setSortingProgram(prev_0 => prev_0.map(p_1 => p_1.value === sortItem.value ? {
      ...p_1,
      active: true
    } : {
      ...p_1,
      active: false
    }));
    setComparings(prev_1 => sortComparingsHelper(prev_1, sortItem.value));
  }, []);
  const onCleanComparing = useCallback(param => {
    setComparings(prev_2 => param === 'cleanAll' ? [] : prev_2.filter(item => item.guid !== param.guid));
  }, []);
  const onOpenCompareModal = useCallback(() => {
    setIsShowCompareModal({
      mounted: true,
      visible: false
    });
    requestAnimationFrame(() => {
      setIsShowCompareModal(prev_3 => ({
        ...prev_3,
        visible: true
      }));
    });
  }, []);
  const onComparePrograms = useCallback((event, program) => {
    const checked = event.target.checked;
    setComparings(prev_4 => {
      if (checked) return [...prev_4, program];
      return prev_4.filter(item_0 => item_0.guid !== program.guid);
    });
  }, []);
  const onOpenForm = useCallback(program_0 => {
    setIsShowFormModal({
      mounted: true,
      visible: false
    });
    setModalFormDataData(() => {
      const initialPaymentPercent = filters.realtyCost > 0 ? Math.round(filters.initialPayment * 100 / filters.realtyCost) : 0;
      return {
        type: programTypes.find(p_2 => p_2.active)?.label,
        programName: program_0.name,
        initialPaymentPercent,
        ...filters
      };
    });
    requestAnimationFrame(() => {
      setIsShowFormModal(prev_5 => ({
        ...prev_5,
        visible: true
      }));
    });
  }, [filters, programTypes]);
  return <div className={cn(styles.terms_calc_container, styles.grid)}>
      <Filters flat={flat} filters={filters} ranges={ranges} programTypes={programTypes} setFilters={setFilters} onSelectProgram={onSelectProgram} onOpenForm={onOpenForm} isLoading={isLoading} />
      <ProgramsList programs={programs} comparings={comparings} onComparePrograms={onComparePrograms} onOpenForm={onOpenForm} onOpenComparigsModal={onOpenCompareModal} onCleanComparings={() => onCleanComparing('cleanAll')} isLoading={isLoading} />

      {isShowCompareModal.mounted && <ComparingModal isVisible={isShowCompareModal.visible} comparings={comparings} sortingProgram={sortingProgram} onClose={() => setIsShowCompareModal(prev_6 => ({
      ...prev_6,
      visible: false
    }))} onAfterClose={() => setIsShowCompareModal({
      mounted: false,
      visible: false
    })} onClean={onCleanComparing} onSort={onSelectSortComparings} onOpenForm={onOpenForm} />}

      {isShowFormModal.mounted && <FormMortgageModal isVisible={isShowFormModal.visible} onClose={() => setIsShowFormModal(prev_7 => ({
      ...prev_7,
      visible: false
    }))} onAfterClose={() => setIsShowFormModal({
      mounted: false,
      visible: false
    })} programTypes={programTypes} modalFormData={modalFormData} formName={modalFormName} />}
    </div>;
};
export default NewCalculator;